import {
  Flex,
  IconButton,
  Popover,
  PopoverOpenChangeDetails,
  Portal,
  Tag,
  Text,
} from '@chakra-ui/react';
import { NODE_HEIGHT_PX } from '@features/create/constants';
import EditConditions from '@features/create/nodes/EditConditions';
import type { SignalType } from '@features/create/CreateSignal';
import { CommodityBlock, UpdateBlock } from '@features/create/types';
import { Handle, NodeProps, Position } from '@xyflow/react';
import { findProcurementItem, ProcurementItem } from 'helpers/fakeData';
import { toPxString } from 'helpers/styles';
import useControlledPopover from 'hooks/useControlledPopover';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { OpenSearchProps, SearchableEntity } from 'stores/pageStore';
import { usePageStore } from 'stores/rootStore';
import { TrashIcon } from 'vectors';
import RawArrowIcon from 'vectors/Arrowhead';

const MAX_TAG_WIDTH_PX = 120;

interface CommodityNodeProps extends NodeProps {
  data: {
    block: CommodityBlock;
    onDelete: () => void;
    updateBlock: (id: string, update: UpdateBlock) => void;
    openSearch: (props: OpenSearchProps) => void;
    signalType: SignalType;
  };
}

interface SelectedCommoditySummaryProps {
  commodity?: ProcurementItem;
  openSearch: (props: OpenSearchProps) => void;
  signalType: SignalType;
}
const SelectedCommoditySummary = ({ commodity, openSearch, signalType }: SelectedCommoditySummaryProps) => {
  const [openedPopoverForTheFirstTime, setOpenedPopoverForTheFirstTime] = useState(false);
  const {
    open,
    onClose: closePopover,
    onOpen: openPopover,
    triggerRef,
    contentRef,
  } = useControlledPopover();

  useEffect(() => {
    if (!open && !openedPopoverForTheFirstTime) {
      setOpenedPopoverForTheFirstTime(true);
      openPopover();
    }
  }, [open, openedPopoverForTheFirstTime]);

  const onOpenChange = useCallback(
    (details: PopoverOpenChangeDetails) => {
      const { open } = details;
      if (open) {
        openPopover();
      } else {
        closePopover();
      }
    },
    [openPopover, closePopover],
  );

  if (commodity == null) {
    return (
      <Flex align="center" gap={2} px={3} py={2}>
        <Text fontWeight="medium" fontSize="sm">
          Select commodity
        </Text>
        <RawArrowIcon direction="down" boxSize="8px" />
      </Flex>
    );
  }

  return (
    <Popover.Root
      open={open}
      positioning={{ placement: 'bottom-start' }}
      onOpenChange={onOpenChange}
    >
      <Popover.Trigger>
        <Flex
          align="center"
          gap={2}
          cursor="pointer"
          fontWeight="medium"
          h="full"
          px={3}
          py={2}
          ref={triggerRef}
          flexWrap="wrap"
        >
          <Tag.Root
            size="sm"
            variant="subtle"
            bgColor="transparent"
            borderRadius="2xl"
            borderColor="border.light"
            borderWidth="1px"
            px={3}
            py={1}
            boxShadow="xs"
            maxWidth={toPxString(MAX_TAG_WIDTH_PX)}
          >
            <Tag.Label textAlign="left" fontWeight="bold">
              {commodity.name}
            </Tag.Label>
          </Tag.Root>
          <Text fontWeight="medium" fontSize="2xs">
            at
          </Text>
          <Tag.Root
            size="sm"
            variant="subtle"
            bgColor="transparent"
            borderRadius="2xl"
            borderColor="border.light"
            borderWidth="1px"
            px={3}
            py={1}
            boxShadow="xs"
          >
            <Tag.Label>Price</Tag.Label>
          </Tag.Root>
          <Text fontWeight="medium" fontSize="2xs">
            in
          </Text>
          <Tag.Root
            size="sm"
            variant="subtle"
            bgColor="transparent"
            borderRadius="2xl"
            borderColor="border.light"
            borderWidth="1px"
            px={3}
            py={1}
            boxShadow="xs"
          >
            <Tag.Label>Days</Tag.Label>
          </Tag.Root>
          <RawArrowIcon direction="down" boxSize="8px" />
        </Flex>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner mt={2}>
          <Popover.Content
            borderRadius="xl"
            boxShadow="sm"
            w={{ base: 'calc(100vw - 32px)', sm: '500px' }}
            maxW="500px"
            bg="surface.background"
            ref={contentRef}
          >
            <EditConditions signalType={signalType} commodityName={commodity.name} />
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};

const CommodityNode: React.FC<CommodityNodeProps> = ({ data }) => {
  const { block, onDelete, openSearch, updateBlock, signalType } = data;
  const pageStore = usePageStore();
  const selectedSearchBlockId = pageStore.selectedSearchBlockId();
  const selectedSearchItems = pageStore.selectedSearchItems();

  const handleContainerClick = useCallback(() => {
    if (block.commodity != null) {
      return;
    }
    openSearch({ searchableEntities: [SearchableEntity.Commodity], blockId: block.id });
  }, [block.commodity, openSearch, block.id]);

  useEffect(() => {
    if (selectedSearchBlockId == null || selectedSearchBlockId !== block.id) {
      return;
    }
    const selectedItems = selectedSearchItems ?? [];
    const selected = selectedItems.find((item) => item.entity === SearchableEntity.Commodity);
    if (selected == null) {
      return;
    }

    updateBlock(block.id, { commodity: findProcurementItem(selected.id, selected.label) });
    pageStore.clearSearchNavigationState();
  }, [selectedSearchBlockId, selectedSearchItems, block.id]);

  return (
    <Flex position="relative" align="center" gap={4}>
      <Flex
        bg="surface.muted"
        borderRadius="full"
        gap={2}
        minH={toPxString(NODE_HEIGHT_PX)}
        border="1px solid"
        borderColor="border.light"
        onClick={handleContainerClick}
      >
        <SelectedCommoditySummary commodity={block.commodity} openSearch={openSearch} signalType={signalType} />
        <Handle type="target" position={Position.Left} style={{ visibility: 'hidden' }} />
      </Flex>
      <IconButton
        size={{ base: '2xs', sm: 'md' }}
        variant="surfaceOutline"
        borderRadius="full"
        _hover={{ color: 'red.500' }}
        onClick={onDelete}
        position={{ base: 'absolute', sm: 'static' }}
        top={{ base: '-8px', sm: 'auto' }}
        right={{ base: '-8px', sm: 'auto' }}
      >
        <TrashIcon />
      </IconButton>
    </Flex>
  );
};

export default observer(CommodityNode);
